// controls/person.js — champ complexe "Personne"
import React, { useState, useEffect } from "react";
import CommonControls from "./inc/Common";
import { BiUser } from "react-icons/bi";

/**
 * Création du champ complexe "person"
 */
export function createPersonField() {
  return {
    type: "object",
    title: "Personne",
    "x-ui": "person",
    properties: {
      includeCivility: true,
      labels: {
        civility: "Civilité",
        name: "Nom",
        firstname: "Prénom",
      },
    },
  };
}

/**
 * Éditeur du champ complexe "Person"
 */
export function EditPersonField({ field, onChange }) {
  const [includeCivility, setIncludeCivility] = useState(
    field.properties?.includeCivility ?? true
  );
  const [labels, setLabels] = useState(
    field.properties?.labels || {
      civility: "Civilité",
      name: "Nom",
      firstname: "Prénom",
    }
  );

  // 🔄 synchronisation vers le parent
  useEffect(() => {
    const updated = {
      ...field,
      properties: { includeCivility, labels },
    };
    onChange(updated);
  }, [includeCivility, labels]);

  const updateLabel = (key, value) =>
    setLabels((prev) => ({ ...prev, [key]: value }));

  return (
    <>
      <CommonControls field={field} onChange={onChange} />

      <div className="mb-3">
        <label className="form-label fw-bold d-flex align-items-center gap-2">
          <BiUser size={18} /> Configuration du champ "Personne"
        </label>

        {/* Inclure civilité */}
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="includeCivility"
            checked={includeCivility}
            onChange={(e) => setIncludeCivility(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="includeCivility">
            Inclure le champ Civilité
          </label>
        </div>

        {/* Libellés */}
        <div className="">
          {includeCivility && (
            <div className="mb-3">
              <label className="form-label">Libellé Civilité</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={labels.civility}
                onChange={(e) => updateLabel("civility", e.target.value)}
              />
            </div>
          )}
          <div className="mb-3">
            <label className="form-label">Libellé Nom</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={labels.name}
              onChange={(e) => updateLabel("name", e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Libellé Prénom</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={labels.firstname}
              onChange={(e) => updateLabel("firstname", e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Aperçu du rendu */}
      {/*
      <div className="border rounded bg-light p-3 mt-3">
        <small className="text-muted d-block mb-2">Aperçu :</small>
        <form className="row g-2">
          {includeCivility && (
            <div className="col-md-2">
              <label className="form-label">{labels.civility}</label>
              <select className="form-select form-select-sm">
                <option value="">—</option>
                <option value="Mme">Mme</option>
                <option value="M.">M.</option>
              </select>
            </div>
          )}
          <div className="col-md-5">
            <label className="form-label">{labels.name}</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Nom"
            />
          </div>
          <div className="col-md-5">
            <label className="form-label">{labels.firstname}</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Prénom"
            />
          </div>
        </form>
      </div>
      */}
    </>
  );
}

export const editPersonField = (props) => <EditPersonField {...props} />;


/* =========================================================
   🧩 FRONT — rendu du champ complexe "Person"
   ========================================================= */
export const RenderPersonField = ({
  field,
  value = {},
  onChange,
  className,
  errorMessage   // <--- AJOUT
}) => {

  const props = field.properties || {};
  const includeCivility = props.includeCivility ?? true;
  const labels = props.labels || {
    civility: "Civilité",
    name: "Nom",
    firstname: "Prénom",
  };

  const civility = value?.civility || "";
  const name = value?.name || "";
  const firstname = value?.firstname || "";

  // gestion du changement local
  const handleChange = (key, val) => {
    const updated = { civility, name, firstname, [key]: val };
    onChange?.(updated);
  };

  // classe à appliquer aux inputs en cas d'erreur
  const invalidClass = errorMessage ? "is-invalid" : "";

  return (
    <div
      className={`
        fb-field fb-person
        ${field["x-bloc-class"] || "mb-3"}
        ${className || ""}
      `}
    >

      {/* Label global */}
      {field.title && (
        <label className={`form-label fw-bold d-block mb-2 ${errorMessage ? "text-danger" : ""}`}>
          {field.title}
        </label>
      )}

      {/* Champs */}
      <div className={`row g-2 ${field["x-control-class"] || ""}`}>

        {/* Civilité */}
        {includeCivility && (
          <div className="col-md-2">
            <label className="form-label">{labels.civility}</label>
            <select
              className={`form-select form-select-sm ${invalidClass}`}
              value={civility}
              onChange={(e) => handleChange("civility", e.target.value)}
            >
              <option value="">—</option>
              <option value="Mme">Mme</option>
              <option value="M.">M.</option>
            </select>
          </div>
        )}

        {/* Nom */}
        <div className={includeCivility ? "col-md-5" : "col-md-6"}>
          <label className="form-label">{labels.name}</label>
          <input
            type="text"
            className={`form-control form-control-sm ${invalidClass}`}
            placeholder={labels.name}
            value={name}
            onChange={(e) => handleChange("name", e.target.value)}
          />
        </div>

        {/* Prénom */}
        <div className={includeCivility ? "col-md-5" : "col-md-6"}>
          <label className="form-label">{labels.firstname}</label>
          <input
            type="text"
            className={`form-control form-control-sm ${invalidClass}`}
            placeholder={labels.firstname}
            value={firstname}
            onChange={(e) => handleChange("firstname", e.target.value)}
          />
        </div>

      </div>

      {/* Message d’erreur global */}
      {errorMessage && (
        <div className="invalid-feedback d-block mt-1">
          {errorMessage}
        </div>
      )}

    </div>
  );
};
